import type { RefObject } from 'react'
import type { TableProps } from 'antd'
import type { Dayjs } from 'dayjs'
import { useState, useEffect } from 'react'
import { useDomSize } from 'src/hooks'
import { $consts } from 'src/plugins'
import { isEqual } from 'lodash'

/**
 * 格式化时间控件值为 YYYY-MM-DD HH:mm:ss 字符串
 * @param targetMoment 
 * @param dateType 
 * @param isEndDate 如果是结束日期，则结束日期需要是当天的 23:59:59
 * @returns 
 */
export const getFormatMoment = (
  targetMoment: Dayjs,
  options: {
    dateType?: string,
    isEndDate?: boolean,
    targetFormat?: string
  }
) => {
  if (!targetMoment) return targetMoment

  const { dateType, isEndDate, targetFormat = 'YYYY-MM-DD HH:mm:ss' } = options
  switch (dateType) {
    case $consts['COMMON/DATE_TYPE_SECOND']:
    case $consts['COMMON/DATE_TYPE_DAY_SECOND']:
    case $consts['COMMON/DATE_TYPE_RANGE_DAY_SECOND']:
      // 精确到秒的时候，就不需要处理 isEndDate 了
      return targetMoment.format(targetFormat)
    case $consts['COMMON/DATE_TYPE_RANGE_DAY_MINUTE']:
      return targetMoment[isEndDate ? 'endOf' : 'startOf']('minute').format(targetFormat)
    case $consts['COMMON/DATE_TYPE_DAY']:
    case $consts['COMMON/DATE_TYPE_RANGE_DAY']:
      return targetMoment[isEndDate ? 'endOf' : 'startOf']('day').format(targetFormat)
    case $consts['COMMON/DATE_TYPE_WEEK']:
      return `${targetMoment.startOf('week').format(targetFormat)},${targetMoment.endOf('week').format(targetFormat)}`
    case $consts['COMMON/DATE_TYPE_RANGE_WEEK']:
      return targetMoment[isEndDate ? 'endOf' : 'startOf']('week').format(targetFormat)
    case $consts['COMMON/DATE_TYPE_MONTH']:
    case $consts['COMMON/DATE_TYPE_RANGE_MONTH']:
      return targetMoment[isEndDate ? 'endOf' : 'startOf']('month').format(targetFormat)
    case $consts['COMMON/DATE_TYPE_QUARTER']:
      return `${targetMoment.startOf('quarter').format(targetFormat)},${targetMoment.endOf('quarter').format(targetFormat)}`
    case $consts['COMMON/DATE_TYPE_RANGE_QUARTER']:
      return targetMoment[isEndDate ? 'endOf' : 'startOf']('quarter').format(targetFormat)
    case $consts['COMMON/DATE_TYPE_YEAR']:
    case $consts['COMMON/DATE_TYPE_RANGE_YEAR']:
      return targetMoment[isEndDate ? 'endOf' : 'startOf']('year').format(targetFormat)
    default:
      // dateType 没有，则默认按 Antd 的 DatePicker 处理，DatePicker 的默认 picker 类型为 date，也即 DATE_TYPE_DAY
      return targetMoment.startOf('day').format(targetFormat)
  }
}

export type UseScrollConfig = {
  validScroll?: boolean | number,
  fixHeight?: number,
  /**
   * antd@5.x
   * 建议 scroll x 配合 ellipsis 使用
   * 
   * x: true 时，配合 column ellipsis: true
   * 可以实现表格内容不换行横向滚动，此时 column width 设置不生效
   * 
   * x: '100%' 时，配合 column ellipsis: true
   * 可以实现表格内容不换行横向滚动，此时表格每一列宽度相同，内容会被省略显示
   * 此时可以设置 column width，内容是否会被省略显示取决于 width 设置的大小
   * 此时，如果某一列没有配置 ellipsis: true（但至少需要一列有设置 ellipsis），那么 width 的设置依然有效，并且内容能换行展示
   */
  percentX?: boolean,
  maxContent?: boolean,
}

export const useTableScroll = (
  wrapEl: RefObject<HTMLDivElement>,
  config?: UseScrollConfig,
) => {
  const wrapSize = useDomSize(wrapEl)

  const [scroll, setScroll] = useState<TableProps<any>['scroll']>(undefined)
  useEffect(() => {
    const dom = wrapEl.current

    const isValidScroll = config?.validScroll ?? true
    if (dom && isValidScroll) {
      const thH = dom.querySelector('.ant-table-thead')?.clientHeight ?? 0
      const tbH = dom.querySelector('.ant-table-tbody')?.clientHeight ?? 0
      const tbF = dom.querySelector('.ant-table-footer')?.clientHeight ?? 0
      const paginationH = dom.querySelector('.ant-pagination')?.clientHeight ?? 0
      const pageH = paginationH ? paginationH + 32 : 0 // 32 为 margin 值
      const scrollH = wrapSize.clientHeight - thH - tbF - pageH - (config?.fixHeight ?? 0)
      setScroll((prev: any) => {
        const target = {
          x: config?.percentX ? '100%' : config?.maxContent ? 'max-content' : true,
          y: scrollH < tbH ? scrollH : undefined
        }
        return isEqual(prev, target) ? prev : target
      })
    } else {
      setScroll(undefined)
    }
  }, [wrapSize.clientHeight, JSON.stringify(config)])

  return scroll
}